import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import SearchFilters from '../../components/Search/SearchFilters';
import sessionService, { Session } from '../../services/session.service';

const SearchSessions: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load public sessions
  useEffect(() => {
    const loadSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await sessionService.getAvailableSessions();
        setSessions(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((session: Session) => {
          session.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (err) {
        console.error('Error loading sessions:', err);
        setError('Erreur lors du chargement des séances');
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, []);

  // Filter sessions
  useEffect(() => {
    let filtered = [...sessions];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (session) =>
          session.name?.toLowerCase().includes(query) ||
          session.description?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((session) =>
        selectedTags.every((tag) => session.tags?.includes(tag))
      );
    }

    setFilteredSessions(filtered);
  }, [searchQuery, selectedTags, sessions]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="séances"
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredSessions.length} séance{filteredSessions.length > 1 ? 's' : ''} trouvée{filteredSessions.length > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredSessions.map((session) => (
                <Grid item xs={12} sm={6} md={4} key={session.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {session.name}
                      </Typography>

                      {session.created_by_name && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Par {session.created_by_name}
                        </Typography>
                      )}

                      {session.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 1,
                          }}
                        >
                          {session.description}
                        </Typography>
                      )}

                      {session.items && session.items.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {session.items.length} élément{session.items.length > 1 ? 's' : ''}
                        </Typography>
                      )}

                      {session.tags && session.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {session.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredSessions.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucune séance ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchSessions;
